import { useEffect, useState } from "react";
import { api, money, formatApiErrorDetail } from "@/api";
import { toast } from "sonner";
import { DollarSign, PiggyBank, CalendarCheck, Users, Star, Tent, RotateCcw, ShieldAlert, KeyRound } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Legend } from "recharts";

export default function AdminInsights() {
  const [d, setD] = useState(null);
  const [failed, setFailed] = useState(false);
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  const [busy, setBusy] = useState(false);

  const load = () => {
    setFailed(false);
    api.get("/admin/insights").then((r) => setD(r.data))
      .catch((e) => { setFailed(true); toast.error(formatApiErrorDetail(e.response?.data?.detail)); });
  };
  useEffect(() => { load(); }, []);

  const changePassword = async () => {
    setBusy(true);
    try {
      await api.post("/auth/change-password", pw);
      toast.success("Password updated");
      setPw({ current_password: "", new_password: "" });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (failed) return (
    <div data-testid="insights-error" className="p-20 text-center">
      <p className="text-sm text-zinc-400">Couldn't load insights.</p>
      <button data-testid="insights-retry-btn" onClick={load}
        className="mt-5 bg-[#00e5ff] text-black text-xs font-bold uppercase px-6 py-3 hover:bg-[#00b3cc] transition-colors duration-200">Try again</button>
    </div>
  );
  if (!d) return <div className="p-20 text-center text-zinc-500">Loading insights…</div>;

  const cards = [
    { icon: DollarSign, label: "Gross volume", value: money(d.gross_volume), sub: `${d.paid_bookings} paid bookings` },
    { icon: PiggyBank, label: "Platform earnings", value: money(d.platform_earnings), sub: `${money(d.client_fees)} client + ${money(d.coach_fees)} coach` },
    { icon: CalendarCheck, label: "Average booking", value: money(d.average_booking), sub: `${d.unique_clients} unique families` },
    { icon: Users, label: "Active coaches", value: d.active_coaches, sub: `${d.pending_approvals} awaiting approval` },
    { icon: Star, label: "Average rating", value: d.average_rating || "—", sub: `${d.reviews} reviews written` },
    { icon: Tent, label: "Published camps", value: d.published_camps, sub: `${d.campers} campers booked` },
    { icon: RotateCcw, label: "Refunded", value: money(d.refunded), sub: `${d.cancelled_bookings} cancellations` },
  ];

  const input = "mt-1.5 w-full bg-black border border-white/10 text-sm px-3 py-2.5 focus:border-[#00e5ff] focus:outline-none focus:ring-1 focus:ring-[#00e5ff] transition-colors duration-200";

  return (
    <div data-testid="admin-insights-page" className="max-w-[1400px] mx-auto px-6 lg:px-10 py-14">
      <span className="text-[11px] uppercase tracking-[0.3em] text-[#ff3b30]">Admin</span>
      <h1 className="display text-4xl sm:text-5xl uppercase tracking-tighter font-bold mt-3">Platform insights</h1>
      <p className="text-sm text-zinc-400 mt-4 max-w-xl leading-relaxed">Everything happening across GoalieHub — money, coaches, families and camps.</p>

      <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map(({ icon: Icon, label, value, sub }) => (
          <div key={label} data-testid={`insight-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="border border-white/10 bg-[#121212] p-5">
            <Icon className="w-4 h-4 text-[#00e5ff]" />
            <p className="display text-3xl font-bold mt-3 leading-none">{value}</p>
            <p className="text-[10px] uppercase tracking-widest text-zinc-500 mt-2">{label}</p>
            <p className="text-[10px] text-zinc-600 mt-1">{sub}</p>
          </div>
        ))}
      </div>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 border border-white/10 bg-[#121212] p-6">
          <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-4">Volume and earnings by month</p>
          {d.monthly.length === 0 ? (
            <p className="text-xs text-zinc-500">No paid bookings yet.</p>
          ) : (
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={d.monthly}>
                  <XAxis dataKey="month" stroke="#71717a" fontSize={11} />
                  <YAxis stroke="#71717a" fontSize={11} />
                  <Tooltip contentStyle={{ background: "#0a0a0a", border: "1px solid #27272a" }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="volume" name="Volume" fill="#00e5ff" />
                  <Bar dataKey="earnings" name="Your earnings" fill="#ff3b30" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        <div className="border border-white/10 bg-[#121212] p-6">
          <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-4">Bookings by type</p>
          <div className="space-y-3">
            {d.by_kind.length === 0 && <p className="text-xs text-zinc-500">Nothing yet.</p>}
            {d.by_kind.map((k) => (
              <div key={k.kind} data-testid={`kind-${k.kind}`} className="flex items-center justify-between text-sm">
                <span className="text-zinc-300 capitalize">{k.kind}</span>
                <span className="font-semibold text-[#00e5ff]">{k.bookings}</span>
              </div>
            ))}
          </div>
          <p className="text-[10px] uppercase tracking-widest text-zinc-500 mt-8 mb-3">By discipline</p>
          <div className="space-y-3">
            {d.by_discipline.map((x) => (
              <div key={x.category} data-testid={`discipline-${x.category.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="flex items-center justify-between text-sm">
                <span className="text-zinc-300">{x.category}</span>
                <span className="font-semibold">{money(x.volume)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <h2 className="display text-2xl uppercase tracking-tight font-bold mt-14">Top coaches</h2>
      <div className="mt-4 border border-white/10">
        {d.top_coaches.length === 0 && <p className="p-10 text-sm text-zinc-500">No revenue yet.</p>}
        {d.top_coaches.map((c) => (
          <div key={c.coach_name} data-testid={`top-coach-${c.coach_name.replace(/\s+/g, "-")}`} className="p-5 bg-[#121212] border-b border-white/10 grid grid-cols-1 md:grid-cols-12 gap-4 items-center">
            <div className="md:col-span-5">
              <p className="display uppercase font-bold text-lg leading-none">{c.coach_name}</p>
              <p className="text-[11px] text-zinc-500 mt-1">{c.category}</p>
            </div>
            <div className="md:col-span-3 text-sm text-zinc-300">{money(c.volume)} volume</div>
            <div className="md:col-span-2 text-sm font-semibold text-[#00e5ff]">{money(c.earnings)} to you</div>
            <div className="md:col-span-2 md:text-right text-xs text-zinc-500">{c.bookings} booking(s)</div>
          </div>
        ))}
      </div>

      <h2 className="display text-2xl uppercase tracking-tight font-bold mt-14">Latest activity</h2>
      <div className="mt-4 border border-white/10">
        {d.recent.length === 0 && <p className="p-10 text-sm text-zinc-500">No activity yet.</p>}
        {d.recent.map((r, i) => (
          <div key={i} data-testid={`activity-row-${i}`} className="p-5 bg-[#121212] border-b border-white/10 grid grid-cols-1 md:grid-cols-12 gap-3 items-center">
            <div className="md:col-span-4 text-sm">
              <span className="font-semibold">{r.athlete_name || r.user_name}</span>
              <span className="block text-[11px] text-zinc-500">with {r.coach_name}</span>
            </div>
            <div className="md:col-span-4 text-xs text-zinc-400">{r.service_title}</div>
            <div className="md:col-span-2 text-sm font-semibold text-[#00e5ff]">{money(r.amount)}</div>
            <div className="md:col-span-2 md:text-right text-[10px] uppercase tracking-widest text-zinc-500">{r.status?.replace("_", " ")}</div>
          </div>
        ))}
      </div>

      <div className="mt-14 border border-[#ff3b30]/40 bg-[#121212] p-6 max-w-lg">
        <div className="flex items-center gap-2 text-[#ff3b30]">
          <ShieldAlert className="w-4 h-4" />
          <p className="text-[10px] uppercase tracking-widest">Secure your admin account</p>
        </div>
        <h2 className="display text-xl uppercase tracking-tight font-bold mt-3">Change password</h2>
        <p className="text-xs text-zinc-500 mt-2 leading-relaxed">Your account still uses the seeded starter password. Change it now that the site is live.</p>
        <label className="block mt-4"><span className="text-[10px] uppercase tracking-widest text-zinc-500">Current password</span>
          <input data-testid="current-password-input" type="password" className={input} value={pw.current_password}
            onChange={(e) => setPw({ ...pw, current_password: e.target.value })} /></label>
        <label className="block mt-3"><span className="text-[10px] uppercase tracking-widest text-zinc-500">New password (min 8 characters)</span>
          <input data-testid="new-password-input" type="password" className={input} value={pw.new_password}
            onChange={(e) => setPw({ ...pw, new_password: e.target.value })} /></label>
        <button data-testid="change-password-btn" disabled={busy} onClick={changePassword}
          className="mt-5 bg-[#00e5ff] text-black font-bold uppercase tracking-wide px-6 py-3 flex items-center gap-2 hover:bg-[#00b3cc] disabled:opacity-50 transition-colors duration-200">
          <KeyRound className="w-4 h-4" /> Update password
        </button>
      </div>
    </div>
  );
}
